import { RemoteAudioPlayer } from '../common/player';
import { IS_INSERTABLE_STREAM_SUPPORTED, IS_SEI_SUPPORTED, isWebCodecSupported } from '../common/rtc-detection';
import { PublishedUser, IRoom, MediaType, AudioCodecPipelineType, UserRole, RemoteTrackEvent, IRemoteTrack } from '../enums';
import { AudioDecodeProcessorParam } from '../manager/audio-manager';
import VolumeMeter from '../common/volume-meter/volume-meter';
import { RemoteTrack } from './remote-track';
import innerEmitter from '../inner-emitter/inner-emitter';
import { INNER_EVENT } from '../inner-emitter/inner-type';

export class RemoteAudioTrack extends RemoteTrack<'audio'> {
  volume = 0; // [0, 1]
  mediaType = MediaType.AUDIO;
  stat: RemoteAudioStat = {
    bytesReceived: 0,
    packetsReceived: 0,
    packetsLost: 0,
    end2EndDelay: 0, // 目前该指标是后台统计出的上行发包 到 下行收包的延迟，应该是 p2p 网络传输延迟，真正的端到端延迟还要加上上行采集 + 前处理 + 编码延迟 & 下行 jitterBuffer 延迟
    jitterBufferDelay: 0
  };
  // 0 - 100
  get dbVolume() {
    return VolumeMeter.isRunning ? this.player.pipeline.volumeMeter.getVolumeDb() : Math.floor(Math.max(10 * Math.log10(this.volume) + 100, 0));
  }
  declare player: RemoteAudioPlayer;
  constructor(room: IRoom, user: PublishedUser) {
    super(room, user, MediaType.AUDIO);
    this.manager = room.audioManager;
  }

  onPlayerError(error: MediaError) {
    if (this.enableDecodeFrame) {
      this._log.warn('use audio decoder');
      this.room.enableInsertableStreams();
    }
  }
  get enableDecodeFrame() {
    if (!this.manager) return false;
    return this.manager.decodePipeline.some(x => x) || (this.player.element?.error?.code === MediaError.MEDIA_ERR_DECODE && isWebCodecSupported().AudioDecoder && IS_INSERTABLE_STREAM_SUPPORTED);
  }
  get enableDecryptFrame() {
    return this.manager && !!this.manager.decodePipeline[AudioCodecPipelineType.ENCRYPT_AND_DECRYPT];
  }
  // decodeFrame(frame: RTCEncodedAudioFrame) {
  //   if (!this.manager) return frame;
  //   let decodedFrame: RTCEncodedAudioFrame | void = frame;
  //   for (const decoder of this.manager.decodePipeline) {
  //     if (!decoder) continue;
  //     decodedFrame = decoder({ frame: decodedFrame, track: this });
  //     if (!decodedFrame) return;
  //   }
  //   return decodedFrame;
  decodeFrame(frame: RTCEncodedAudioFrame) {
    if (!this.manager) return frame;
    let decodedFrame: RTCEncodedAudioFrame | void = frame;
    for (const [index, decoder] of this.manager.decodePipeline.entries()) {
      if (!decoder) continue;
      const param: AudioDecodeProcessorParam = { frame, track: this };
      if (index === AudioCodecPipelineType.NTP_TO_AUDIO_FRAME && this.isAvailable && this.room.role === UserRole.AUDIENCE) {
        param.onAudioFrameNTPTime = ({ ntp, frame, hasLeavingTag }) => {
          this.emit(RemoteTrackEvent.AUDIO_FRAME_WITH_NTP, { ntp, frame, hasLeavingTag });
        };
      }
      decodedFrame = decoder(param);
      if (!decodedFrame) return;
    }
    return decodedFrame;
  }
  getAudioLevel(): number {
    if (!this.isAvailable) return 0;
    const result = this.volume || super.getAudioLevel();
    return result > 1 ? 1 : result;
  }
  getInternalAudioLevel() {
    if (!this.isAvailable) return 0;
    return super.getInternalAudioLevel();
  }
  get isRemotePublished() {
    return this.user.muteState.audioAvailable;
  }
}
